import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';

/**
 * Local copy of the clinic / plan-options modal, previously rendered from
 * `@ideabytes/groupbenefitz_common_ui` (`ClinicsModule` -> `lib-clinics`).
 *
 * ROOT CAUSE of the "modal reopens after Confirm" bug: on Confirm the component
 * attaches the chosen options to the plan row in sessionStorage, but it looked
 * only for `planLevel == 18`. Which plan levels actually carry clinic options is
 * backend-driven via `clinicOptionsApplicablePlanLevels`, and on this form the
 * clinic plan (BYOND Executive Care) is level 107. Nothing matched, the options
 * were never attached, and the host - corp-plans-enrollment, which filters on
 * `clinicOptionsApplicablePlanLevels` - saw an empty `options` array on Next and
 * reopened the modal. Forever. See resolveTargetPlanLevels() below.
 *
 * Additional hardening over the library version:
 *  - persists BEFORE closing the modal, so a failure leaves the modal open
 *    instead of closing with the selection unsaved;
 *  - matches radios by the real `option.id` rather than assuming ids are 1..N;
 *  - updates `plansummary` and `plansummarymain` independently instead of
 *    overwriting one with the other;
 *  - drops a dead guard that ran `plandetailsobj[11].includes(...)` behind an
 *    always-true condition and threw when element 11 was missing;
 *  - warns on every failure path instead of failing silently.
 *
 * The selector, inputs, outputs and markup are unchanged, so every existing
 * `<lib-clinics>` template keeps working as-is.
 */
@Component({
  selector: 'lib-clinics',
  standalone: false,
  templateUrl: './clinics.component.html',
})
export class ClinicsComponent {
  @Input() optionstitle: any[] = [];
  @Input() lang: any = {};
  @Input() userForm: any = {};
  @Input() selectedProvinceId: any;
  @Input() optionmessage: any;
  @Input() planOptionsModel: any;
  @Output() planOptionsModelChange = new EventEmitter<any>();
  @Output() closeClicked = new EventEmitter<any>();
  @Output() confirmClicked = new EventEmitter<any>();
  @Input() plandetailsobjvalue: any;
  @Input() currChild: any = {};
  @Input() currChildType: any = 'SPOUSE';
  @Input() currChildIndex: any = 1;
  @Input() planScopeDetails: any[] = [];

  /**
   * Inert inputs. The host templates bind `[_CustomDatePipe]`, `[_decimalPipe]`,
   * `[onCloseOptions]` and `[onConfirmOptions]`. The library component never
   * declared them either -- the bindings were silently tolerated because
   * `lib-clinics` was an unknown element under CUSTOM_ELEMENTS_SCHEMA. Now that
   * this is a real declared component they must exist or the build fails
   * (NG8002), so they are kept as no-ops.
   */
  @Input() _CustomDatePipe: any;
  @Input() _decimalPipe: any;
  @Input() onCloseOptions: any;
  @Input() onConfirmOptions: any;

  @ViewChild('optionNumberInput') optionNumberInput: any;
  @ViewChild('optionPlanDetailsObj') optionPlanDetailsObj: any;

  private readonly summaryKeys = ['plansummary', 'plansummarymain'];

  constructor(private renderer: Renderer2, private elementRef: ElementRef) {}

  /** The option groups currently rendered in the modal. */
  private get optionGroups(): any[] {
    return Array.isArray(this.optionstitle) ? this.optionstitle : [];
  }

  /** The checked radio for one option group, scoped to this component's DOM. */
  private checkedRadioFor(groupId: any): HTMLInputElement | null {
    return this.elementRef.nativeElement.querySelector(
      '.optionselectionmethod-' + groupId + ':checked'
    );
  }

  private readSummary(key: string): any[] {
    try {
      const parsed = JSON.parse(sessionStorage.getItem(key) || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  closeoptionsselection(): void {
    this.closeClicked.emit();
    this.planOptionsModel = false;
    this.planOptionsModelChange.emit(false);

    try {
      const planCheck = this.renderer.selectRootElement(
        '#plancheck' + this.plandetailsobjvalue[7]
      );
      planCheck.click();
    } catch (e) {
      // The plan checkbox is not present on every host screen.
    }

    // Clear selections by the groups' real ids. The old loop walked 1..N and
    // threw on the first id that did not exist.
    for (const group of this.optionGroups) {
      const radio = this.checkedRadioFor(group && group.id);
      if (radio) {
        this.renderer.setProperty(radio, 'checked', false);
      }
    }
  }

  getGroupedByProvince(clinics: any[]): any[] {
    if (!clinics || clinics.length === 0) {
      return [];
    }
    const provinceMap = new Map<any, any>();
    clinics.forEach((clinic) => {
      const provinceId = clinic.provinceId;
      const provinceName = clinic.provinceName || 'Other';
      if (!provinceMap.has(provinceId)) {
        provinceMap.set(provinceId, {
          provinceId: provinceId,
          provinceName: provinceName,
          clinics: [],
        });
      }
      provinceMap.get(provinceId).clinics.push(clinic);
    });

    const selectedProvince = this.selectedProvinceId;
    return Array.from(provinceMap.values()).sort((a, b) => {
      // "Other" (null province) always last, the member's province first.
      if (a.provinceId === null) return 1;
      if (b.provinceId === null) return -1;
      if (a.provinceId === selectedProvince) return -1;
      if (b.provinceId === selectedProvince) return 1;
      return 0;
    });
  }

  planoptionselection(
    event: any,
    optionvalue: any,
    optionid: any,
    optionvalueid: any,
    index: any,
    className: any
  ): void {
    // The radios in a group share a class rather than a name attribute, so the
    // group has to be cleared manually before checking the clicked one.
    const elements = this.elementRef.nativeElement.querySelectorAll(
      '.' + className + optionid
    );
    elements.forEach((element: any) => {
      this.renderer.setProperty(element, 'checked', false);
    });

    const selected = this.elementRef.nativeElement.querySelector(
      '#planselectionvalue-' + optionvalueid + '-' + optionid
    );
    if (selected) {
      this.renderer.setProperty(selected, 'checked', true);
    }

    this.optionmessage = '';
  }

  confirmoptionsselection(): void {
    const groups = this.optionGroups;

    // Validate against the real option groups, keyed by their backend id.
    let complete = groups.length > 0;
    for (const group of groups) {
      if (!this.checkedRadioFor(group && group.id)) {
        complete = false;
        break;
      }
    }
    if (!complete) {
      console.warn('[clinics] confirm blocked - every option group needs a selection', {
        groupCount: groups.length,
        groupIds: groups.map((g: any) => g && g.id),
      });
      this.optionmessage = this.lang.Please_select_all_plan_options;
      return;
    }
    this.optionmessage = '';

    // Build the selected-option array from each group's checked radio.
    const options: any[] = [];
    for (const group of groups) {
      const sel = this.checkedRadioFor(group && group.id);
      if (!sel) {
        continue;
      }
      let selected: any;
      try {
        selected = JSON.parse(sel.value);
      } catch (e) {
        console.warn('[clinics] could not parse selected option value', {
          groupId: group && group.id,
          value: sel.value,
        });
        continue;
      }
      selected.name = selected.planOptionName;
      selected.planOptionsValueId = selected.id;
      options.push(selected);
    }

    if (options.length === 0) {
      // Unreachable after the validation above, but this was the exact silent
      // failure behind the reopen loop -- do not let it pass quietly again.
      console.warn('[clinics] confirm produced an EMPTY option array', {
        groupIds: groups.map((g: any) => g && g.id),
      });
      this.optionmessage = this.lang.Please_select_all_plan_options;
      return;
    }

    // Persist BEFORE closing the modal. The library version closed first and
    // then threw partway through, leaving the selection unsaved.
    const summaries = this.summaryKeys.map((key) => ({
      key,
      rows: this.readSummary(key),
    }));

    const hasScopedPlan = summaries.some((s) =>
      s.rows.some((plan: any) => plan.planLevel == 15)
    );
    // planScopeDetails is shared parent state -- update it once, not per store.
    if (hasScopedPlan) {
      this.applyPlanScopeDetails(options);
    }

    // Apply to every summary store that actually holds the plan, rather than
    // overwriting one store with the other. The library version wrote
    // `plansummary` over `plansummarymain`, so a plan present in only one of
    // them lost its options and the host reopened the modal.
    const targetLevels = this.resolveTargetPlanLevels(summaries);
    let attached = false;
    for (const summary of summaries) {
      if (summary.rows.length === 0) {
        continue;
      }
      for (const plan of summary.rows) {
        if (targetLevels.some((lvl: any) => lvl == plan.planLevel)) {
          plan.options = options;
          attached = true;
        }
      }
      sessionStorage.setItem(summary.key, JSON.stringify(summary.rows));
    }

    if (!attached && !hasScopedPlan) {
      console.warn('[clinics] options were not attached to any plan row', {
        targetLevels,
        plansummary: summaries[0].rows.map((p: any) => p.planLevel),
        plansummarymain: summaries[1].rows.map((p: any) => p.planLevel),
      });
    }

    this.planOptionsModel = false;
    this.planOptionsModelChange.emit(false);
    this.confirmClicked.emit(this.optionstitle);
  }

  /**
   * Which plan levels the chosen options belong to.
   *
   * The backend drives this through `clinicOptionsApplicablePlanLevels` (the host
   * stores it on load and its "Next" validation filters on it). The library
   * component instead hardcoded `planLevel == 18`, so on forms where the clinic
   * plan has a different level - e.g. BYOND Executive Care is 107 - the options
   * were never attached and the host reopened the modal forever.
   *
   * Order: configured levels -> legacy 18 -> the level of the plan this modal
   * was actually opened for (element 15 of the "##" plan-details string).
   */
  private resolveTargetPlanLevels(
    summaries: { key: string; rows: any[] }[]
  ): any[] {
    let configured: any[] = [];
    try {
      const raw = JSON.parse(
        sessionStorage.getItem('clinicOptionsApplicablePlanLevels') || '[]'
      );
      if (Array.isArray(raw)) {
        configured = raw;
      }
    } catch (e) {
      configured = [];
    }
    if (configured.length > 0) {
      return configured;
    }

    const hasLegacyLevel = summaries.some((s) =>
      s.rows.some((plan: any) => plan.planLevel == 18)
    );
    if (hasLegacyLevel) {
      return [18];
    }

    const openedFor = Array.isArray(this.plandetailsobjvalue)
      ? parseInt(this.plandetailsobjvalue[15], 10)
      : NaN;
    if (!isNaN(openedFor)) {
      return [openedFor];
    }

    return [18];
  }

  /** Mirror the chosen clinic / opt-in onto the current member in planScopeDetails. */
  private applyPlanScopeDetails(options: any[]): void {
    let existingIndex: number;
    if (this.currChildType == 'SPOUSE' || this.currChildType == 'SELF') {
      existingIndex = this.planScopeDetails.findIndex(
        (person: any) => person.relationShip === this.currChildType
      );
    } else {
      existingIndex = this.planScopeDetails.findIndex(
        (person: any) =>
          person.relationShip === this.currChildType &&
          person.childIndex === this.currChildIndex
      );
    }

    const clinicOption = options.find((item: any) => item.planOptionsId == 1);
    const optinOption = options.find((item: any) => item.planOptionsId == 2);
    const planOptions = [
      {
        id: clinicOption?.id ?? '',
        planOptionsId: clinicOption?.planOptionsId ?? '',
        clinicName: clinicOption?.value ?? '',
        clinic: clinicOption?.value ?? '',
        location: clinicOption?.location ?? '',
      },
      {
        optinName: optinOption?.name ?? '',
        optinValue: optinOption?.value ?? '',
        planOptionsId: optinOption?.planOptionsId ?? '',
        id: optinOption?.id ?? '',
      },
    ];

    if (existingIndex === -1) {
      this.planScopeDetails.push({
        firstName: this.currChild.firstName,
        lastName: this.currChild.lastName,
        relationShip: this.currChildType,
        // NOTE: kept as-is from the library version. This stores the child
        // object while the lookup above compares it against currChildIndex, so
        // a pushed child is never matched again. Pre-existing behaviour, left
        // alone here so the saved payload shape does not change.
        childIndex: this.currChild,
        options: planOptions,
      });
    } else {
      this.planScopeDetails[existingIndex].options = planOptions;
    }
  }
}
